import { useMutation, useQueryClient } from '@tanstack/react-query'

import config from '@config'

import {
  SportEntitySubscription,
  SportEntitySubscriptionsAPIResponse,
} from './useSportEntitySubscriptions'

const {
  nemosyne: { baseUrl, deleteEntitySubscriptionUrl },
} = config

export type UnsubscribeFromEntityType = Pick<
  SportEntitySubscription,
  'entityId' | 'entityType'
>

const unsubscribeFromEntity = async ({
  entityId,
  entityType,
}: UnsubscribeFromEntityType) => {
  const response = await fetch(
    `${baseUrl}${deleteEntitySubscriptionUrl.replace('{ENTITY_TYPE}', entityType).replace('{ENTITY_ID}', entityId)}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  )

  if (!response.ok) {
    throw new Error(`Server responded with ${response.status}`)
  }

  return response.json() as Promise<SportEntitySubscription>
}

const useSportEntitySubscriptionsUnsubscribe = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: unsubscribeFromEntity,
    onMutate: async (unsubscribeEntityData) => {
      const { entityType } = unsubscribeEntityData

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: ['sport-entity-subscriptions', entityType],
      })

      // Snapshot the previous value
      const previousEntitySubscriptionData = queryClient.getQueryData<
        SportEntitySubscriptionsAPIResponse | undefined
      >(['sport-entity-subscriptions', entityType])

      // Optimistically update to the new value
      queryClient.setQueryData<SportEntitySubscriptionsAPIResponse | undefined>(
        ['sport-entity-subscriptions', entityType],
        (oldData) =>
          oldData?.filter(
            (entitySubscription) =>
              entitySubscription.entityId !== unsubscribeEntityData.entityId,
          ),
      )

      // Return a context object with the snapshotted value
      return { previousEntitySubscriptionData }
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (error, unsubscribeEntityData, context) => {
      queryClient.setQueryData(
        ['sport-entity-subscriptions', unsubscribeEntityData.entityType],
        context?.previousEntitySubscriptionData,
      )
      console.error(
        'An error happened while unsubscribing from the entity:',
        error,
      )
    },
    // Always refetch after error or success:
    onSettled: (_data, _error, unsubscribeEntityData) =>
      queryClient.invalidateQueries({
        queryKey: [
          'sport-entity-subscriptions',
          unsubscribeEntityData.entityType,
        ],
      }),
  })
}

export default useSportEntitySubscriptionsUnsubscribe
